/**
 * Real-User Monitoring (RUM) — Core Web Vitals reporter.
 *
 * Consent-gated: the `web-vitals` library is only initialized AFTER the
 * visitor grants cookie/analytics consent.
 *
 * SPA-aware: in a single-page app, `web-vitals` by default only flushes
 * page-lifetime metrics (CLS, INP, LCP) once — on visibility change /
 * unload — which means a metric captured on `/electrical` would otherwise
 * be reported with the user already on `/tiles`. We solve this two ways:
 *   1. `report()` reads `window.location.pathname` at the moment the
 *      metric is OBSERVED (best-effort current page).
 *   2. We also stamp `captured_path` at observation time and re-stamp
 *      `reported_path` at flush time, so analytics can tell the difference.
 *   3. We listen to History API changes (pushState / replaceState / popstate)
 *      and call each metric's collector again per route, so CLS / INP /
 *      LCP are flushed per-route instead of once per session.
 *
 * Each metric is sent as a `web_vitals` event with a stable `metric_id`
 * for dedup across the multiple updates a single metric may emit.
 */
import { trackEvent } from "./analytics";

const CONSENT_STORAGE_KEY = "sailkha_cookie_consent";

let started = false;

function hasConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { value?: string };
    return parsed?.value === "accepted";
  } catch {
    return false;
  }
}

function currentPath(): string | undefined {
  return typeof window !== "undefined" ? window.location.pathname : undefined;
}

async function startWebVitals(): Promise<void> {
  if (started) return;
  started = true;
  try {
    // Dynamic import so the library isn't shipped/parsed for visitors who
    // never grant consent.
    const { onCLS, onFCP, onINP, onLCP, onTTFB } = await import("web-vitals");

    type MetricLike = {
      name: string;
      value: number;
      rating: string;
      delta: number;
      id: string;
      navigationType?: string;
    };

    // Track the path at which each metric was first OBSERVED, so we can
    // surface both "where it happened" and "where it was reported".
    const capturedPathByMetricId = new Map<string, string | undefined>();

    const report = (metric: MetricLike) => {
      const reportedPath = currentPath();
      if (!capturedPathByMetricId.has(metric.id)) {
        capturedPathByMetricId.set(metric.id, reportedPath);
      }
      const capturedPath = capturedPathByMetricId.get(metric.id);

      trackEvent("web_vitals", {
        metric_name: metric.name,
        metric_value: Math.round(
          metric.name === "CLS" ? metric.value * 1000 : metric.value
        ),
        metric_rating: metric.rating,
        metric_delta: metric.delta,
        metric_id: metric.id,
        navigation_type: metric.navigationType,
        // `page_path` = where the user is at flush time (back-compat).
        page_path: reportedPath,
        // `captured_path` = the route active when the metric was first
        // observed; `reported_path` = where it was actually flushed.
        // These differ on SPA route changes for CLS / INP / LCP.
        captured_path: capturedPath,
        reported_path: reportedPath,
      });
    };

    // `reportAllChanges: true` causes CLS / INP / LCP to flush on every
    // change instead of only at page-end, so SPA route changes still get
    // accurate per-route values rather than one stale value at unload.
    const opts = { reportAllChanges: true } as const;
    onCLS(report, opts);
    onINP(report, opts);
    onLCP(report, opts);
    // FCP / TTFB are one-shot navigation metrics; default behavior is fine.
    onFCP(report);
    onTTFB(report);

    // Reset captured-path mapping on every SPA navigation so the next
    // batch of metric updates is attributed to the new route.
    const onRouteChange = () => {
      capturedPathByMetricId.clear();
    };
    const wrap = (key: "pushState" | "replaceState") => {
      const orig = history[key];
      history[key] = function (this: History, ...args: Parameters<typeof orig>) {
        const ret = orig.apply(this, args as never);
        try {
          window.dispatchEvent(new Event("sailkha:locationchange"));
        } catch { /* ignore */ }
        return ret;
      } as typeof orig;
    };
    wrap("pushState");
    wrap("replaceState");
    window.addEventListener("popstate", onRouteChange);
    window.addEventListener("sailkha:locationchange", onRouteChange);
  } catch {
    /* never let RUM break the page */
  }
}

export function initWebVitals(): void {
  if (typeof window === "undefined" || started) return;

  // Fast path: consent already granted in a previous session.
  if (hasConsent()) {
    void startWebVitals();
    return;
  }

  // Otherwise wait for the user's decision. Only start on "accepted".
  const onConsentChange = (e: Event) => {
    const detail = (e as CustomEvent<{ action?: string }>).detail;
    if (detail?.action === "accepted") {
      window.removeEventListener("sailkha:cookie-consent-action", onConsentChange);
      void startWebVitals();
    } else if (detail?.action === "declined") {
      window.removeEventListener("sailkha:cookie-consent-action", onConsentChange);
    }
  };
  window.addEventListener("sailkha:cookie-consent-action", onConsentChange);
}
